import type { DefaultOptionType } from 'antd/es/select';

// Human-friendly labels for each extra column
export const EXTRA_COLS_LABELS: Record<string, string> = {
  sspBidRequestError: 'SSP Bid Request Error',
  sspBidResponse: 'SSP Bid Response',
  sspBidResponseBid: 'SSP Bid Response (Bid)',
  noBid: 'No Bid',
  dspBidRequestTimeout: 'DSP Bid Request Timeout',
  dspBidRequestError: 'DSP Bid Request Error',
  dspBidResponse: 'DSP Bid Response',
  dspBidResponseInvalid: 'DSP Bid Response Invalid',
  dspBidResponseValid: 'DSP Bid Response Valid',
  dspBidResponseNoBid: 'DSP No Bid',
  validationNotPassed: 'Validation Not Passed',
  unsupportedTrafficType: 'Unsupported Traffic Type',
  filterBefore: 'Filtered Before',
  filterOut: 'Filtered Out',
  filterTrafficTypeBanner: 'Filter: Traffic Type · Banner',
  filterTrafficTypeNative: 'Filter: Traffic Type · Native',
  filterTrafficTypeVideo: 'Filter: Traffic Type · Video',
  filterPlatformSiteMobile: 'Filter: Platform · Site Mobile',
  filterPlatformApp: 'Filter: Platform · App',
  filterPlatformSiteDesktop: 'Filter: Platform · Site Desktop',
  filterSecurityInsecure: 'Filter: Insecure Security',
  filterGeoCountry: 'Filter: Geo Country',
  filterSspGeoCountry: 'Filter: SSP Geo Country',
  filterDspQpsLimit: 'Filter: DSP QPS Limit',
  filterWhiteListOs: 'Filter: Whitelist OS',
  filterWhiteListSizes: 'Filter: Whitelist Sizes',
  filterBlackListSsp: 'Filter: Blacklist SSP',
  filterWhiteListDsp: 'Filter: Whitelist DSP',
  filterMinTmax: 'Filter: Min tmax',
  filterMaxBidfloor: 'Filter: Max bidfloor',
  filterWhiteListConnType: 'Filter: Whitelist Connection Type',
  filterPorn: 'Filter: Adult Content',
  filterBlackListPublisher: 'Filter: Blacklist Publisher',
  filterWhiteListPublisher: 'Filter: Whitelist Publisher',
  filterBlackListSites: 'Filter: Blacklist Sites',
  filterWhiteListSites: 'Filter: Whitelist Sites',
  filterBlackListBundle: 'Filter: Blacklist Bundle',
  filterWhiteListBundle: 'Filter: Whitelist Bundle',
  filterAfter: 'Filtered After',
  filterBlackListSspCrid: 'Filter: Blacklist SSP CRID',
  filterBlackListSspDomain: 'Filter: Blacklist SSP Domain',
  filterBlockedAdvDomain: 'Filter: Blocked Advertiser Domain',
  filterBadPrice: 'Filter: Bad Price',
  filterUnsupportedCurrency: 'Filter: Unsupported Currency',
  filterOptimizer: 'Filter: Optimizer',
  filterSspOptimizer: 'Filter: SSP Optimizer',
  swapperRequest: 'Swapper Requests',
  swapperMiss: 'Swapper Misses',
  swapperMissExpired: 'Swapper Miss · Expired',
  swapperMissNotYetStored: 'Swapper Miss · Not Yet Stored',
  doubleImpressions: 'Double Impressions',
  doubleWins: 'Double NURL',
  doubleBurls: 'Double BURL',
  missingImpWinPrice: 'Missing Impression Win Price',
  sspBidRequestTimeout: 'SSP Bid Request Timeout',
  sspDailySpendLimit: 'SSP Daily Spend Limit',
  sspTotalSpendLimit: 'SSP Total Spend Limit',
  dspBidRequestUsersync: 'DSP Usersync Requests',
  dspBidRequestUsersyncNotFound: 'DSP Usersync Not Found',
  dspBidRequestUsersyncTimeout: 'DSP Usersync Timeout',
  dspBidRequestUsersyncError: 'DSP Usersync Error',
  dspBidRequestUsersyncBefore: 'DSP Usersync Before',
};

export const getExtraColLabel = (key: string): string => EXTRA_COLS_LABELS[key] || key;

// Build groups in a readable order
const ORDERED_GROUPS: Array<[string, string[]]> = [
  [
    'SSP Requests/Responses',
    [
      'sspBidRequestError',
      'sspBidResponse',
      'sspBidResponseBid',
      'noBid',
      'sspBidRequestTimeout',
      'sspDailySpendLimit',
      'sspTotalSpendLimit',
    ],
  ],
  [
    'DSP Requests/Responses',
    [
      'dspBidRequestTimeout',
      'dspBidRequestError',
      'dspBidResponse',
      'dspBidResponseInvalid',
      'dspBidResponseValid',
      'dspBidResponseNoBid',
    ],
  ],
  [
    'Usersync',
    [
      'dspBidRequestUsersync',
      'dspBidRequestUsersyncNotFound',
      'dspBidRequestUsersyncTimeout',
      'dspBidRequestUsersyncError',
      'dspBidRequestUsersyncBefore',
    ],
  ],
  ['Validation & Unsupported', ['validationNotPassed', 'unsupportedTrafficType']],
  ['Traffic Type Filters', ['filterTrafficTypeBanner', 'filterTrafficTypeNative', 'filterTrafficTypeVideo']],
  ['Platform Filters', ['filterPlatformSiteMobile', 'filterPlatformApp', 'filterPlatformSiteDesktop']],
  ['Geo Filters', ['filterGeoCountry', 'filterSspGeoCountry']],
  [
    'Limits & Pricing',
    ['filterDspQpsLimit', 'filterMinTmax', 'filterMaxBidfloor', 'filterBadPrice', 'filterUnsupportedCurrency'],
  ],
  [
    'Publisher/Site Filters',
    [
      'filterBlackListPublisher',
      'filterWhiteListPublisher',
      'filterBlackListSites',
      'filterWhiteListSites',
      'filterBlackListBundle',
      'filterWhiteListBundle',
      'filterBlackListSspCrid',
      'filterBlackListSspDomain',
    ],
  ],
  ['Security & Content', ['filterSecurityInsecure', 'filterPorn', 'filterBlockedAdvDomain']],
  ['Optimizer & Timing', ['filterBefore', 'filterAfter', 'filterOut', 'filterOptimizer', 'filterSspOptimizer']],
  ['Double/Missing', ['doubleImpressions', 'doubleWins', 'doubleBurls', 'missingImpWinPrice']],
  ['Swapper', ['swapperRequest', 'swapperMiss', 'swapperMissExpired', 'swapperMissNotYetStored']],
];

export const buildExtraColsGroups = (availableCols: string[]): DefaultOptionType[] => {
  const available = new Set(availableCols);

  const groups = ORDERED_GROUPS.map(([groupLabel, keys]) => ({
    label: groupLabel,
    options: keys.filter((k) => available.has(k)).map((k) => ({ label: EXTRA_COLS_LABELS[k] || k, value: k })),
  })).filter((g) => g.options.length > 0);

  // Any columns from the API that aren't in any group get added to an "Other" group
  const groupedCols = new Set(ORDERED_GROUPS.flatMap(([, keys]) => keys));
  const ungrouped = availableCols.filter((k) => !groupedCols.has(k));
  if (ungrouped.length > 0) {
    groups.push({
      label: 'Other',
      options: ungrouped.map((k) => ({ label: EXTRA_COLS_LABELS[k] || k, value: k })),
    });
  }

  return groups;
};
